import * as React from "react";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";

interface MenuItemsProps {
  options: Array<{ value: string; label: string }>;
  placeholder: string;
  onChange: (value: string) => void;
  isActive: boolean;
  noFirstOption?: boolean;
}

export const MenuItems: React.FC<MenuItemsProps> = ({
  options,
  placeholder,
  onChange,
  isActive,
  noFirstOption,
}) => {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  const [selectOptions, setSelectOptions] = React.useState([
    { label: placeholder, value: placeholder },
  ]);
  React.useEffect(() => {
    const newOptions = noFirstOption
      ? [...options]
      : [{ label: placeholder, value: placeholder }, ...options];
    setSelectOptions([...newOptions]);
  }, [options]);
  const handleClick = () => {
    if (selectedIndex === 0) {
      return;
    }
    onChange(selectOptions[selectedIndex].value);
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number
  ) => {
    setSelectedIndex(index);
    onChange(selectOptions[index].value);
    setOpen(false);
  };

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  return (
    <React.Fragment>
      <ButtonGroup
        variant={isActive ? "contained" : "outlined"}
        ref={anchorRef}
        aria-label="split button"
      >
        <Button onClick={handleClick}>
          {selectOptions[selectedIndex]?.label || ""}
        </Button>
        <Button
          size="small"
          aria-controls={open ? "split-button-menu" : undefined}
          aria-expanded={open ? "true" : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <ArrowDropDownIcon />
        </Button>
      </ButtonGroup>
      <Popper
        sx={{
          zIndex: 5,
        }}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin:
                placement === "bottom" ? "center top" : "center bottom",
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu" autoFocusItem>
                  {selectOptions.map((option, index) => (
                    <MenuItem
                      key={option.value}
                      disabled={!noFirstOption ? index === 0 : false}
                      selected={index === selectedIndex}
                      onClick={(event) => handleMenuItemClick(event, index)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </React.Fragment>
  );
};
export default MenuItems;
